import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const authContext = useAuth();
  const user = authContext?.user;
  const updateLanguage = authContext?.updateLanguage;
  
  // Get browser language and map it to supported languages
  const getBrowserLanguage = useCallback(() => {
    const browserLang = navigator.language || navigator.languages?.[0] || 'en';
    const langCode = browserLang.split('-')[0]; // Get language code (e.g., 'en' from 'en-US')
    
    // Check if the browser language is supported
    const supportedLanguages = ['en', 'es', 'de', 'ru', 'zh', 'ar', 'fr', 'pt'];
    return supportedLanguages.includes(langCode) ? langCode : 'en';
  }, []);

  // Initialize with browser language or stored preference
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // First check localStorage for saved preference (new key)
    const savedLanguage = localStorage.getItem('salesign-language');
    if (savedLanguage) {
      return savedLanguage;
    }
    
    // Migrate from old key if it exists
    const oldSavedLanguage = localStorage.getItem('landspot-language');
    if (oldSavedLanguage) {
      localStorage.setItem('salesign-language', oldSavedLanguage);
      localStorage.removeItem('landspot-language');
      return oldSavedLanguage;
    }
    
    // Then check user preference
    if (user?.preferred_language) {
      return user.preferred_language;
    }
    
    // Finally use browser language
    return getBrowserLanguage();
  });

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
  ];

  // Set language from user preference or browser language
  useEffect(() => {
    let targetLanguage = currentLanguage;
    
    if (user?.preferred_language) {
      targetLanguage = user.preferred_language;
    } else if (!localStorage.getItem('salesign-language') && !localStorage.getItem('landspot-language')) {
      // Only use browser language if no saved preference exists (check both old and new keys)
      targetLanguage = getBrowserLanguage();
    }
    
    if (targetLanguage !== currentLanguage) {
    setCurrentLanguage(targetLanguage);
    }
    if (i18n.language !== targetLanguage) {
    i18n.changeLanguage(targetLanguage);
    }
    localStorage.setItem('salesign-language', targetLanguage);
    document.documentElement.lang = targetLanguage;
  }, [user?.preferred_language, i18n, currentLanguage, getBrowserLanguage]);

  const changeLanguage = (languageCode) => {
    setCurrentLanguage(languageCode);
    i18n.changeLanguage(languageCode);
    localStorage.setItem('salesign-language', languageCode);
    document.documentElement.lang = languageCode;
    
    // Update user's preferred language if authenticated
    if (user && updateLanguage) {
      updateLanguage(languageCode);
    }
  };

  const value = {
    currentLanguage,
    languages,
    changeLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
